import {
  ArrowUpRight,
  BookOpen,
  Download,
  ExternalLink,
  Info,
  Loader2,
  PartyPopper,
  Plus,
  Rocket,
  Sparkles,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { ClientBrandPreview } from "@/components/client-brand-preview";
import { ClientSelect } from "@/components/client-select";
import { Github } from "@/components/icons/github";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  cancelProjectScaffold,
  checkEmailSender,
  createProject,
  generateProject,
  getProjectVibe,
  isApiError,
  type ProjectVibe,
  suggestDemoPassword,
  useClients,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import { InstructionsDialog } from "./instructions-dialog";
import { type CompletionData, ScaffoldProgress } from "./scaffold-progress";

type Step = "describe" | "review" | "progress" | "complete";

interface NewProjectDialogProps {
  trigger?: React.ReactNode;
  onProjectCreated: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** sessionStorage key used to carry the in-progress prompt across the round
 * trip to /clients when the user creates a brand-new client mid-flow. */
const NEW_PROJECT_PROMPT_KEY = "new-project:prompt";

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;
const DEMO_PW_BLOCKED_RE = /['"\\`$]/;
// Intentionally rejects control characters (newline, carriage return, NUL) in
// the demo password — they would break the generated .env / shell quoting.
// eslint-disable-next-line no-control-regex
const DEMO_PW_CONTROL_RE = /[\n\r\x00]/;

// The description is sent as one line (BOIE-302): the template renders it inside
// one-line strings, where a line break or control character breaks the file.
// Same rule as the backend's CreateProjectRequest (`_one_line`), which applies it
// again: whitespace runs (line breaks and tabs included) become one space, the
// other control characters, U+FEFF and lone surrogates are dropped. Both classes
// are spelled out as in the backend, character for character, because
// JavaScript's and Python's \s differ (U+FEFF against U+001C-U+001F and U+0085).
// The `u` flag makes \p{Cs} match a lone surrogate only, so emoji survive.
const DESCRIPTION_WHITESPACE_RE =
  // eslint-disable-next-line no-control-regex
  /[\t\n\x0b\x0c\r\x1c-\x20\x85\xa0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/g;
// eslint-disable-next-line no-control-regex
const DESCRIPTION_CONTROL_RE = /[\x00-\x1f\x7f-\x9f\u2028\u2029\ufeff\p{Cs}]/gu;
// Stopgap until the template escapes the description (BOIE-261): it renders it raw
// inside double-quoted strings, where an ASCII double quote or a backslash breaks the
// file after the GitHub repo exists (BOIE-375). Apostrophes, backticks and typographic
// quotes stay. Same class as the backend's `_TEMPLATE_UNSAFE_RE`; BOIE-302 removes both.
const DESCRIPTION_TEMPLATE_UNSAFE_RE = /["\\]/g;

// Always among a project's allowed email domains; the backend adds it to every list.
const BOI_EMAIL_DOMAIN = "boardofinnovation.com";

function toOneLine(text: string): string {
  return text
    .replace(DESCRIPTION_WHITESPACE_RE, " ")
    .replace(DESCRIPTION_CONTROL_RE, "")
    .replace(DESCRIPTION_TEMPLATE_UNSAFE_RE, "")
    .replace(DESCRIPTION_WHITESPACE_RE, " ")
    .trim();
}

// What an internationalized domain holds: ASCII letters, digits, dots and
// hyphens, and non-ASCII characters. Anything else (`/`, `:`, `%`, ...) changes
// how `new URL` reads a host, so such a domain is left as it is.
const IDN_DOMAIN_RE = /^[-a-z0-9.\P{ASCII}]+$/u;
const NON_ASCII_RE = /\P{ASCII}/u;
const ASCII_UPPERCASE_RE = /[A-Z]/g;
// A host the URL parser read as an IPv4 address: its last label is all digits.
const IPV4_HOST_RE = /(^|\.)\d+$/;
// email-validator refuses an email whose domain does not end in a letter (no TLD
// does), so such a domain is never offered as an allowed one.
const TLD_END_RE = /[a-z]$/;

/**
 * The part of an email after its last `@`, in its lowercase ASCII form, as the
 * backend compares it with the allowed email domains (BOIE-339). Only ASCII
 * letters are lowercased here; UTS46 (in `new URL`) maps the rest, as on the
 * backend, because `toLowerCase` turns a capital sigma before a hyphen into a
 * final `ς` where UTS46 gives `σ`, another domain.
 */
function emailDomain(email: string): string {
  return toAsciiDomain(
    email
      .slice(email.lastIndexOf("@") + 1)
      .trim()
      .replace(ASCII_UPPERCASE_RE, (c) => c.toLowerCase()),
  );
}

/**
 * The IDNA ASCII form of a domain whose ASCII letters are lowercase
 * (`bücher.de` becomes `xn--bcher-kva.de`), the only form the allowed email
 * domains hold. A domain that is ASCII already, has no ASCII form, or reads as
 * an IPv4 address is returned lowercased, as the backend returns it.
 */
function toAsciiDomain(domain: string): string {
  if (!NON_ASCII_RE.test(domain) || !IDN_DOMAIN_RE.test(domain)) {
    return domain.toLowerCase();
  }
  let hostname: string;
  try {
    hostname = new URL(`http://${domain}`).hostname.toLowerCase();
  } catch {
    return domain.toLowerCase();
  }
  // UTS46 maps fullwidth digits to ASCII ones, so the parser reads a host such
  // as `１２７．０．０．１` as the IPv4 address 127.0.0.1, which is no email domain.
  return IPV4_HOST_RE.test(hostname) ? domain.toLowerCase() : hostname;
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function isValidSlug(slug: string): boolean {
  return SLUG_RE.test(slug);
}

/**
 * `publicSimpleAuth`: a deployed project without Supabase signs in with this
 * password on a public URL, so the template requires 12 characters there and
 * refuses "admin" (BOIE-271). The backend enforces the same rule.
 */
function getDemoPasswordError(pw: string, publicSimpleAuth: boolean): string | null {
  if (pw.length < 8) {
    return "Password must be at least 8 characters.";
  }
  if (publicSimpleAuth && (pw.length < 12 || pw === "admin")) {
    return "A deployed project without Supabase needs a password of at least 12 characters.";
  }
  if (DEMO_PW_CONTROL_RE.test(pw)) {
    return "Password cannot contain control characters.";
  }
  if (DEMO_PW_BLOCKED_RE.test(pw)) {
    return "Password cannot contain quotes, backslashes, dollar signs, or backticks.";
  }
  return null;
}

function getApiErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    const detail = (err.body as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") {
      return detail;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: unknown };
      if (typeof first?.msg === "string") {
        return first.msg;
      }
    }
  }
  return "Failed to create project. Please try again.";
}

function isValidDomain(domain: string): boolean {
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(
    domain.toLowerCase(),
  );
}

const EMAIL_SENDER_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

function isValidEmailSender(localPart: string): boolean {
  return EMAIL_SENDER_RE.test(localPart);
}

const CUSTOM_DOMAIN_SUFFIX = import.meta.env.VITE_CUSTOM_DOMAIN_BASE || "boardofinnovation.ai";

const APP_POLL_INTERVAL_MS = 5000;
const APP_POLL_TIMEOUT_MS = 5 * 60 * 1000; // stop after 5 min
const APP_POLL_TIMEOUT_CUSTOM_DOMAIN_MS = 10 * 60 * 1000; // custom domains need longer for DNS + SSL
const APP_READY_BUFFER_MIN_MS = 15_000; // extra wait after first healthy response
const APP_READY_BUFFER_MAX_MS = 20_000; // to let the deployment fully stabilise

/**
 * Polls the app URL with a HEAD request until it returns a non-error status.
 * After the first healthy response, waits an extra 10-15 s buffer so the
 * deployment is fully stable before the user can open it.
 *
 * Returns "polling" while checking, "almost-ready" during the buffer,
 * "ready" once fully available, or "timeout" if it never came up.
 */
function useAppReady(
  url: string | undefined,
  timeoutMs = APP_POLL_TIMEOUT_MS,
): "polling" | "almost-ready" | "ready" | "timeout" {
  const [status, setStatus] = useState<"polling" | "almost-ready" | "ready" | "timeout">(
    url ? "polling" : "ready",
  );

  const check = useCallback(async (target: string) => {
    try {
      // Use our own backend as a proxy to avoid CORS issues with the
      // deployed Railway URL.  The /api/health-check?url=... endpoint
      // does a server-side HEAD and returns { ok: true/false }.
      const resp = await fetch(`/api/health-check?url=${encodeURIComponent(target)}`);
      if (!resp.ok) {
        return false;
      }
      const data = (await resp.json()) as { ok: boolean };
      return data.ok;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!url) {
      setStatus("ready");
      return;
    }

    setStatus("polling");
    let cancelled = false;
    const start = Date.now();

    const poll = async () => {
      while (!cancelled) {
        const ok = await check(url);
        if (ok && !cancelled) {
          // App responds, but give the deployment a few extra seconds to
          // fully stabilise so users don't land on a 404.
          setStatus("almost-ready");
          const buffer =
            APP_READY_BUFFER_MIN_MS +
            Math.random() * (APP_READY_BUFFER_MAX_MS - APP_READY_BUFFER_MIN_MS);
          await new Promise((r) => setTimeout(r, buffer));
          if (!cancelled) {
            setStatus("ready");
          }
          return;
        }
        if (Date.now() - start > timeoutMs && !cancelled) {
          setStatus("timeout");
          return;
        }
        await new Promise((r) => setTimeout(r, APP_POLL_INTERVAL_MS));
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [url, check, timeoutMs]);

  return status;
}

export function NewProjectDialog({
  trigger,
  onProjectCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: NewProjectDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (v: boolean) => controlledOnOpenChange?.(v) : setInternalOpen;
  const [step, setStep] = useState<Step>("describe");
  const navigate = useNavigate();

  const { data: clients = [] } = useClients();

  // Step 1: Describe
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  // Step 2: Review
  const [projectName, setProjectName] = useState("");
  const [clientSlug, setClientSlug] = useState("");
  const [clientId, setClientId] = useState<number | null>(null);
  const [description, setDescription] = useState("");
  const [includePersonalization, setIncludePersonalization] = useState(true);
  const [includeSupabase, setIncludeSupabase] = useState(true);
  const [includeRailway, setIncludeRailway] = useState(true);
  const [creating, setCreating] = useState(false);
  const [demoEmail, setDemoEmail] = useState("");
  const [demoPassword, setDemoPassword] = useState("");
  const [allowedDomains, setAllowedDomains] = useState<string[]>(["boardofinnovation.com"]);
  const [newDomain, setNewDomain] = useState("");
  const [customSubdomain, setCustomSubdomain] = useState("");
  const [emailSender, setEmailSender] = useState("");
  const [emailSenderChecking, setEmailSenderChecking] = useState(false);
  const [emailSenderAvailable, setEmailSenderAvailable] = useState<boolean | null>(null);
  // Full address returned by the backend (authoritative domain) for the preview.
  const [emailSenderAddress, setEmailSenderAddress] = useState<string | null>(null);
  const [advancedFeatures, setAdvancedFeatures] = useState(false);

  const demoEmailTouched = useRef(false);
  const customSubdomainTouched = useRef(false);
  const emailSenderTouched = useRef(false);

  // Step 3: Progress
  const [projectId, setProjectId] = useState<number | null>(null);

  const [cancelling, setCancelling] = useState(false);

  // Step 4: Complete
  const [completionData, setCompletionData] = useState<CompletionData | null>(null);
  const [vibeData, setVibeData] = useState<ProjectVibe | null>(null);
  const vibeImageReady = useRef(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const [error, setError] = useState("");

  const projectSlug = toSlug(projectName);
  const rawRepoSlug = clientSlug && projectSlug ? `${clientSlug}-${projectSlug}` : "";
  // Truncate to 63 chars (backend max) and strip trailing hyphens from truncation
  const repoSlug = rawRepoSlug.slice(0, 63).replace(/-+$/, "");
  const repoSlugValid = repoSlug.length >= 2 && isValidSlug(repoSlug);
  // What is sent: the textarea keeps the user's line breaks, the request does not.
  const oneLineDescription = toOneLine(description);

  const selectedClient =
    clientId !== null ? (clients.find((c) => c.id === clientId) ?? null) : null;

  function resetState() {
    setStep("describe");
    setPrompt("");
    setProjectName("");
    setClientSlug("");
    setClientId(null);
    setDescription("");
    setIncludePersonalization(true);
    setIncludeSupabase(true);
    setIncludeRailway(true);
    setProjectId(null);
    setCompletionData(null);
    setVibeData(null);
    setError("");
    setGenerating(false);
    setCreating(false);
    setCancelling(false);
    setDemoEmail("");
    setDemoPassword("");
    setAllowedDomains(["boardofinnovation.com"]);
    setNewDomain("");
    setCustomSubdomain("");
    setEmailSender("");
    setEmailSenderChecking(false);
    setEmailSenderAvailable(null);
    setEmailSenderAddress(null);
    setAdvancedFeatures(false);
    demoEmailTouched.current = false;
    customSubdomainTouched.current = false;
    emailSenderTouched.current = false;
    vibeImageReady.current = false;
  }

  async function handleGenerate() {
    if (!prompt.trim()) {
      setError("Please describe your project before generating.");
      return;
    }
    setGenerating(true);
    setError("");

    try {
      const { data: fields } = await generateProject({
        body: {
          prompt: prompt.trim(),
        },
      });
      if (!fields) {
        throw new Error("empty generation response");
      }
      setProjectName(fields.project_name.replace(/[^a-zA-Z0-9 \-&().]/g, ""));
      // If the user already picked a client up front, its real slug wins over
      // whatever the AI guessed from the prompt text -- that keeps the repo
      // slug consistent with the client's actual identity.
      setClientSlug(selectedClient?.slug ?? fields.client_slug);
      // Quotes are stripped until the template escapes the description (BOIE-261).
      setDescription(fields.description.replace(/['\u2018\u2019\u201c\u201d"`]/g, ""));
      setDemoEmail(`${fields.client_slug}@boardofinnovation.com`);
      setDemoPassword(fields.demo_password || "");
      if (fields.allowed_email_domains?.length) {
        setAllowedDomains(fields.allowed_email_domains);
      }
      demoEmailTouched.current = false;
      setStep("review");
    } catch (err) {
      logger.error("[new-project] AI generation failed:", err);
      setError("Failed to generate project fields. Try again or fill in manually.");
    } finally {
      setGenerating(false);
    }
  }

  async function handleSkipToManual() {
    // A pre-picked client seeds the repo slug prefix here too -- the AI
    // generate path does this (see handleGenerate), and skipping straight to
    // manual must not silently drop it, or the Client Identifier lands empty
    // and blocks creation.
    if (selectedClient) {
      setClientSlug(selectedClient.slug);
    }
    setStep("review");

    // The AI path prefills a generated demo password; the manual path gets one
    // from the same generator, so nobody has to make one up. If this fails the
    // field stays empty and the user types their own.
    try {
      const { data } = await suggestDemoPassword();
      if (data) {
        // Never overwrite a password that is already there.
        setDemoPassword((current) => current || data.demo_password);
      }
    } catch (err) {
      logger.error("[new-project] demo password suggestion failed:", err);
    }
  }

  async function handleCreate() {
    if (!projectName.trim() || !repoSlug || !clientSlug.trim() || !oneLineDescription) {
      setError("Please fill in all required fields.");
      return;
    }
    setCreating(true);
    setError("");

    try {
      const { data: project } = await createProject({
        body: {
          project_name: projectName.trim(),
          client_slug: clientSlug.trim(),
          client_id: clientId,
          project_slug: repoSlug,
          description: oneLineDescription,
          python_version: "3.14",
          include_supabase: includeSupabase,
          include_railway: includeRailway,
          include_personalization: includePersonalization,
          demo_email: demoEmail.trim(),
          demo_password: demoPassword,
          allowed_email_domains: allowedDomains,
          custom_subdomain: includeRailway ? customSubdomain || repoSlug : undefined,
          email_sender: includeSupabase ? emailSender.trim() || undefined : undefined,
          include_pr_agents: advancedFeatures,
        },
      });
      if (!project) {
        throw new Error("empty create response");
      }
      setProjectId(project.id);
      setStep("progress");
    } catch (err) {
      logger.error("[new-project] creation failed:", err);
      setError(getApiErrorMessage(err));
      setCreating(false);
    }
  }

  /** Add a lowercase domain to the allowed list; false when it is invalid or already there. */
  function addAllowedDomain(domain: string): boolean {
    if (!domain || !isValidDomain(domain) || allowedDomains.includes(domain)) {
      return false;
    }
    setAllowedDomains((prev) => [...prev, domain]);
    return true;
  }

  function handleAddDomain() {
    if (addAllowedDomain(newDomain.trim().toLowerCase())) {
      setNewDomain("");
    }
  }

  function handleRemoveDomain(domain: string) {
    if (domain === "boardofinnovation.com") {
      return;
    }
    setAllowedDomains((prev) => prev.filter((d) => d !== domain));
  }

  const canGenerate = !generating && !!prompt.trim();
  const clientSlugValid = isValidSlug(clientSlug);
  const demoPasswordError = demoPassword
    ? getDemoPasswordError(demoPassword, !includeSupabase && includeRailway)
    : null;
  // Without Supabase the demo email is the app's login name, which cannot hold
  // "|" (BOIE-271). The backend refuses it too.
  const demoEmailHasPipe = !includeSupabase && demoEmail.includes("|");
  const demoEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(demoEmail);
  // A Supabase scaffold refuses a login from a domain outside its allowed email
  // domains, so a demo admin there could never sign in (BOIE-339). The backend
  // refuses the request too; this says so before Create.
  const demoEmailDomain = emailDomain(demoEmail);
  const demoEmailDomainBlocked =
    includeSupabase &&
    demoEmailValid &&
    demoEmailDomain !== BOI_EMAIL_DOMAIN &&
    !allowedDomains.includes(demoEmailDomain);
  // Adding the domain is offered only when the allowed email domains accept it
  // and the backend would accept the email (never an IPv4 address).
  const demoEmailDomainAddable = isValidDomain(demoEmailDomain) && TLD_END_RE.test(demoEmailDomain);
  const emailSenderValid = isValidEmailSender(emailSender) && emailSenderAvailable !== false;
  const canCreate =
    !creating &&
    !!projectName.trim() &&
    clientSlugValid &&
    repoSlugValid &&
    !!oneLineDescription &&
    demoEmailValid &&
    !demoEmailDomainBlocked &&
    !!demoPassword &&
    !demoPasswordError &&
    !demoEmailHasPipe &&
    (!includeRailway || isValidSlug(customSubdomain || repoSlug)) &&
    (!includeSupabase || emailSenderValid);

  // Restore a prompt stashed before navigating away to create a new client
  // (see the "Need a new client?" link below) so the description survives
  // the round trip back to this dialog.
  useEffect(() => {
    if (!open) {
      return;
    }
    const stashedPrompt = sessionStorage.getItem(NEW_PROJECT_PROMPT_KEY);
    if (stashedPrompt) {
      setPrompt(stashedPrompt);
      sessionStorage.removeItem(NEW_PROJECT_PROMPT_KEY);
    }
  }, [open]);

  function handleCreateNewClient() {
    if (prompt.trim()) {
      sessionStorage.setItem(NEW_PROJECT_PROMPT_KEY, prompt);
    }
    navigate("/clients");
  }

  const handleGenerateRef = useRef(handleGenerate);
  const handleCreateRef = useRef(handleCreate);
  // Keep refs pointing at the latest handlers — written in an effect so the
  // react-hooks/refs rule (no ref writes during render) stays happy.
  useEffect(() => {
    handleGenerateRef.current = handleGenerate;
    handleCreateRef.current = handleCreate;
  });

  useEffect(() => {
    if (step !== "describe") {
      return;
    }
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canGenerate) {
        e.preventDefault();
        handleGenerateRef.current();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [step, canGenerate]);

  useEffect(() => {
    if (step !== "review") {
      return;
    }
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canCreate) {
        e.preventDefault();
        handleCreateRef.current();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [step, canCreate]);

  // Poll for vibe content as soon as we have a projectId (during "progress").
  // The backend starts generating in the background at project creation, so
  // fetching early means the mascot is usually ready by the time the user
  // reaches the completion screen — no fallback flash.
  useEffect(() => {
    if (!projectId || step === "describe" || step === "review") {
      return;
    }
    if (vibeData) {
      return;
    } // already loaded
    const pid = projectId;
    let cancelled = false;

    const FAST_INTERVAL_MS = 3_000;
    const SLOW_INTERVAL_MS = 10_000;
    const FAST_PHASE_MS = 60_000; // first minute: fast polling
    const MAX_WAIT_MS = 3 * 60_000; // give up after 3 minutes

    async function pollVibe() {
      const start = Date.now();

      while (!cancelled && Date.now() - start < MAX_WAIT_MS) {
        try {
          const { data } = await getProjectVibe({ path: { project_id: pid } });
          if (data && !cancelled) {
            // Pre-decode the image so it renders instantly on the complete screen
            const img = new Image();
            img.src = `data:image/png;base64,${data.image_b64}`;
            await img.decode().catch(() => {});
            vibeImageReady.current = true;
            if (!cancelled) {
              setVibeData(data);
            }
          }
          return;
        } catch (err) {
          // Only retry on 404 (not ready yet); stop on auth/server errors
          if (isApiError(err) && err.status !== 404) {
            logger.error("[new-project] Vibe poll failed permanently", err.status);
            return;
          }
          logger.debug(
            "[new-project] Vibe poll — not ready yet (elapsed %ds)",
            Math.round((Date.now() - start) / 1000),
          );
        }

        if (!cancelled) {
          const elapsed = Date.now() - start;
          const interval = elapsed < FAST_PHASE_MS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS;
          await new Promise((r) => setTimeout(r, interval));
        }
      }
    }

    pollVibe();
    return () => {
      cancelled = true;
    };
  }, [step, projectId, vibeData]);

  useEffect(() => {
    if (step !== "review" || demoEmailTouched.current) {
      return;
    }
    if (clientSlug) {
      setDemoEmail(`${clientSlug}@boardofinnovation.com`);
    }
  }, [step, clientSlug]);

  useEffect(() => {
    if (step !== "review" || customSubdomainTouched.current) {
      return;
    }
    if (repoSlug) {
      setCustomSubdomain(repoSlug);
    }
  }, [step, repoSlug]);

  useEffect(() => {
    if (step !== "review" || emailSenderTouched.current) {
      return;
    }
    if (clientSlug) {
      setEmailSender(clientSlug);
    }
  }, [step, clientSlug]);

  // Debounce the email sender availability check so we don't hit the API on
  // every keystroke. Network errors are treated as "unknown" (non-blocking) —
  // only an explicit `available: false` response marks it as taken.
  useEffect(() => {
    if (!includeSupabase) {
      setEmailSenderAvailable(null);
      setEmailSenderAddress(null);
      setEmailSenderChecking(false);
      return;
    }
    const localPart = emailSender.trim().toLowerCase();
    if (!localPart || !isValidEmailSender(localPart)) {
      setEmailSenderAvailable(null);
      setEmailSenderAddress(null);
      setEmailSenderChecking(false);
      return;
    }
    let cancelled = false;
    setEmailSenderChecking(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await checkEmailSender({ query: { local_part: localPart } });
        if (!cancelled) {
          setEmailSenderAvailable(data?.available ?? null);
          setEmailSenderAddress(data?.address ?? null);
        }
      } catch (err) {
        logger.error("[new-project] email sender availability check failed:", err);
        if (!cancelled) {
          setEmailSenderAvailable(null);
          setEmailSenderAddress(null);
        }
      } finally {
        if (!cancelled) {
          setEmailSenderChecking(false);
        }
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      setEmailSenderChecking(false);
    };
  }, [emailSender, includeSupabase]);

  // Always poll the Railway URL first — it's available immediately after
  // deployment. Custom domain DNS + SSL provisioning runs in the background.
  const hasCustomDomain = !!(
    completionData?.railwayAppUrl &&
    completionData?.appUrl &&
    completionData.appUrl !== completionData.railwayAppUrl
  );
  const appReadyStatus = useAppReady(
    step === "complete" ? (completionData?.railwayAppUrl ?? completionData?.appUrl) : undefined,
    APP_POLL_TIMEOUT_MS,
  );
  const customDomainStatus = useAppReady(
    step === "complete" && hasCustomDomain ? completionData?.appUrl : undefined,
    APP_POLL_TIMEOUT_CUSTOM_DOMAIN_MS,
  );

  // Allow opening the app as soon as either endpoint is healthy.
  // This prevents a dead-end where Railway health-check times out but the
  // custom domain is already serving traffic (or vice-versa).
  const canOpenApp =
    appReadyStatus === "ready" || (hasCustomDomain && customDomainStatus === "ready");

  async function handleComplete(data: CompletionData) {
    // If vibe data isn't ready yet, wait up to 3s for it to arrive
    // so the mascot shows immediately instead of flashing the fallback
    if (!vibeImageReady.current) {
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 200));
        if (vibeImageReady.current) {
          break;
        }
      }
    }
    setCompletionData(data);
    setStep("complete");
  }

  function handleDone() {
    setOpen(false);
    resetState();
    onProjectCreated();
  }

  async function handleCancel() {
    if (!projectId) {
      return;
    }
    setCancelling(true);
    setError("");
    try {
      await cancelProjectScaffold({ path: { project_id: projectId } });
      handleDone();
    } catch (err) {
      logger.error("[new-project] cancel failed:", err);
      setError("Could not cancel scaffold. Please try again.");
      setCancelling(false);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v && step === "progress") {
            return;
          }
          if (!v && step === "complete") {
            handleDone();
            return;
          }
          setOpen(v);
          if (!v) {
            resetState();
          }
        }}
      >
        {trigger && !isControlled && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          className="sm:max-w-4xl"
          showCloseButton={step !== "progress"}
          onInteractOutside={(e) => {
            if (step === "progress") {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (step === "progress") {
              e.preventDefault();
            }
          }}
        >
          {step === "describe" && (
            <>
              <DialogHeader>
                <DialogTitle>Describe your project</DialogTitle>
                <DialogDescription>
                  Tell us about your project and we&apos;ll generate the setup for you.
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="prompt">Project Description</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>Describe the client, purpose, and what the project does.</p>
                            <p className="text-background/60 mt-1">
                              AI will use this to generate a project name, slug, and description.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Textarea
                      id="prompt"
                      placeholder="e.g. A customer feedback dashboard for Board of Innovation that aggregates NPS scores and support tickets"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      rows={4}
                      maxLength={5000}
                    />
                    <p className="text-muted-foreground text-right text-xs">
                      {prompt.length}/5,000
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Label>Client (optional)</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>Groups this project under a client and reuses its saved brand.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <ClientSelect value={clientId} onChange={setClientId} className="w-full" />
                    <button
                      type="button"
                      onClick={handleCreateNewClient}
                      className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-2"
                    >
                      Need a new client? Create one →
                    </button>
                  </div>
                  {error && <p className="text-destructive text-sm">{error}</p>}
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleSkipToManual}
                      className="text-muted-foreground hover:text-foreground text-sm underline"
                    >
                      Skip, I&apos;ll fill it in manually
                    </button>
                    <div className="flex items-center gap-2">
                      <Button onClick={handleGenerate} disabled={generating}>
                        {generating ? (
                          <Loader2 className="mr-2 size-4 animate-spin" />
                        ) : (
                          <Sparkles className="mr-2 size-4" />
                        )}
                        {generating ? "Generating..." : "Generate"}
                        {!generating && (
                          <span className="text-muted-foreground ml-1 hidden text-xs sm:inline">
                            ⌘↵
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogBody>
            </>
          )}

          {step === "review" && (
            <>
              <DialogHeader>
                <DialogTitle>Review &amp; confirm</DialogTitle>
                <DialogDescription>
                  Check the details below and create your project.
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                      Project Details
                    </h3>
                    <Separator className="mb-3" />
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="project-name">Project Name</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>A human-readable name for the project.</p>
                            <p className="text-background/60 mt-1">
                              Letters, digits, spaces, hyphens, ampersands, parentheses, and dots.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="project-name"
                      value={projectName}
                      onChange={(e) =>
                        setProjectName(e.target.value.replace(/[^a-zA-Z0-9 \-&().]/g, ""))
                      }
                      placeholder="e.g. Customer Feedback Dashboard"
                      maxLength={100}
                    />
                    <p className="text-muted-foreground -mt-0.5 text-right text-xs">
                      {projectName.length}/100
                    </p>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="client-slug">Client Identifier</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>
                              Short kebab-case identifier for the client, used in the repo name.
                            </p>
                            <p className="text-background/60 mt-1">
                              e.g. board-of-innovation, globex, initech
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="client-slug"
                      value={clientSlug}
                      onChange={(e) =>
                        setClientSlug(
                          e.target.value
                            .toLowerCase()
                            .replace(/\s+/g, "-")
                            .replace(/[^a-z0-9-]/g, "")
                            .replace(/-+/g, "-"),
                        )
                      }
                      placeholder="e.g. board-of-innovation"
                      maxLength={50}
                    />
                    <p className="text-muted-foreground -mt-0.5 text-right text-xs">
                      {clientSlug.length}/50
                    </p>
                    {clientSlug && !clientSlugValid && (
                      <p className="text-destructive text-xs">
                        Client identifier must be 2-63 characters, lowercase alphanumeric and
                        hyphens only, and cannot start or end with a hyphen.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Label>Client</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>Groups this project under a client for filtering.</p>
                            <p className="text-background/60 mt-1">
                              Separate from the client identifier above — this is optional and can
                              be changed later.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <ClientSelect value={clientId} onChange={setClientId} className="w-full" />
                    {selectedClient && (
                      <ClientBrandPreview
                        name={selectedClient.name}
                        primaryColor={selectedClient.primary_color}
                        accentColor={selectedClient.accent_color}
                        logoUrl={selectedClient.logo_url}
                        className="mt-2"
                        message={
                          <>
                            New project will start with{" "}
                            <span className="text-foreground font-medium">
                              {selectedClient.name}
                            </span>
                            &apos;s branding.
                          </>
                        }
                      />
                    )}
                  </div>

                  {repoSlug && (
                    <div>
                      <div
                        className={`flex items-center gap-2 rounded-md border px-3 py-2 ${repoSlug && !repoSlugValid ? "border-destructive" : ""}`}
                      >
                        <Github className="text-muted-foreground size-4 shrink-0" />
                        <span className="text-sm">
                          <span className="text-muted-foreground">
                            Board-of-Innovation-Internal/
                          </span>
                          <span className="font-semibold">{repoSlug}</span>
                        </span>
                      </div>
                      {repoSlug && !repoSlugValid && (
                        <p className="text-destructive mt-1 text-xs">
                          Repository slug must be 2-63 characters, lowercase alphanumeric and
                          hyphens only, and cannot start or end with a hyphen.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="description">Description</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>A short summary visible on GitHub and in the project list.</p>
                            <p className="text-background/60 mt-1">
                              Sent as one line. Double quotes (&quot;) and backslashes (\) are
                              removed for now.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="A concise description of the project"
                      rows={3}
                      maxLength={500}
                    />
                    <p className="text-muted-foreground -mt-0.5 text-right text-xs">
                      {description.length}/500
                    </p>
                  </div>

                  <div>
                    <h3 className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                      Configuration
                    </h3>
                    <Separator className="mb-3" />
                  </div>

                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="include-personalization" className="text-sm font-medium">
                          Personalize project
                        </Label>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="text-muted-foreground size-3.5 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <p>An AI agent researches the client&apos;s brand via web search,</p>
                              <p>then edits CSS colors, fonts, hero text, and meta tags</p>
                              <p>to match the client&apos;s visual identity.</p>
                              <p className="text-background/60 mt-1">
                                Runs in parallel with other setup. Non-blocking if it fails.
                              </p>
                              <p className="text-background/60 mt-1">
                                If the selected client has a saved brand (colors/logo), that is
                                always applied directly — independent of this toggle.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        AI agent customizes colors, font, and branding to match style of the client
                      </p>
                    </div>
                    <Switch
                      id="include-personalization"
                      checked={includePersonalization}
                      onCheckedChange={setIncludePersonalization}
                    />
                  </div>

                  <div className="space-y-3 rounded-lg border p-3">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-sm font-medium">Demo Credentials</Label>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground size-3.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>Initial admin user for the scaffolded app.</p>
                            <p className="text-background/60 mt-1">
                              The email can be fictive, since no verification emails are sent.
                            </p>
                            <p className="text-background/60 mt-1">
                              With Supabase, its domain must be one of the allowed email domains, or
                              the admin cannot sign in.
                            </p>
                            <p className="text-background/60 mt-1">
                              Anyone with this password is an admin of the deployed demo.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="demo-email" className="text-xs">
                        Demo Email
                      </Label>
                      <Input
                        id="demo-email"
                        type="email"
                        value={demoEmail}
                        onChange={(e) => {
                          setDemoEmail(e.target.value);
                          demoEmailTouched.current = true;
                        }}
                        placeholder="e.g. demo@boardofinnovation.com"
                        maxLength={254}
                      />
                      {demoEmailHasPipe && (
                        <p className="text-destructive text-xs">
                          Without Supabase this email is the app&apos;s login name, which cannot
                          contain the character &quot;|&quot;.
                        </p>
                      )}
                      {demoEmail && !demoEmailValid && (
                        <p className="text-destructive text-xs">
                          Please enter a valid email address.
                        </p>
                      )}
                      {demoEmailDomainBlocked && (
                        <div className="space-y-2">
                          <p className="text-destructive text-xs">
                            {demoEmailDomain} is not an allowed email domain, so the demo admin
                            could not sign in.{" "}
                            {demoEmailDomainAddable
                              ? "Allow the domain, or use an address on an allowed domain."
                              : "Use an address on an allowed domain."}
                          </p>
                          {demoEmailDomainAddable && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addAllowedDomain(demoEmailDomain)}
                            >
                              <Plus className="size-4" />
                              Add {demoEmailDomain} to allowed domains
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="demo-password" className="text-xs">
                        Demo Password
                      </Label>
                      <Input
                        id="demo-password"
                        type="text"
                        value={demoPassword}
                        onChange={(e) => setDemoPassword(e.target.value)}
                        placeholder="e.g. three words and a number"
                        maxLength={64}
                      />
                      {demoPassword && demoPasswordError && (
                        <p className="text-destructive text-xs">{demoPasswordError}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="include-supabase" className="text-sm font-medium">
                          Include Supabase
                        </Label>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="text-muted-foreground size-3.5 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <p>Creates a Supabase project with Postgres database,</p>
                              <p>auth (email/password), and applies the schema.</p>
                              <p className="text-background/60 mt-1">
                                Also creates the initial admin user and configures email templates.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Auto-provision a Supabase project with database and auth
                      </p>
                    </div>
                    <Switch
                      id="include-supabase"
                      checked={includeSupabase}
                      onCheckedChange={setIncludeSupabase}
                    />
                  </div>

                  {includeSupabase && (
                    <>
                      <div className="space-y-3 rounded-lg border p-3">
                        <div className="flex items-center gap-1.5">
                          <Label className="text-sm font-medium">Allowed Email Domains</Label>
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="text-muted-foreground size-3.5 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                <p>
                                  Email domains allowed to sign up and sign in to the scaffolded
                                  app.
                                </p>
                                <p className="text-background/60 mt-1">
                                  boardofinnovation.com is always included.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {allowedDomains.map((domain) => (
                            <span
                              key={domain}
                              className="bg-secondary text-secondary-foreground inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm"
                            >
                              {domain}
                              {domain !== "boardofinnovation.com" && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveDomain(domain)}
                                  className="text-muted-foreground hover:text-foreground ml-0.5"
                                >
                                  <X className="size-3" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Input
                            placeholder="e.g. acme.com"
                            value={newDomain}
                            onChange={(e) =>
                              setNewDomain(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ""))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddDomain();
                              }
                            }}
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAddDomain}
                            disabled={!newDomain.trim() || !isValidDomain(newDomain.trim())}
                          >
                            <Plus className="size-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2 rounded-lg border p-3">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor="email-sender" className="text-sm font-medium">
                            Email Sender
                          </Label>
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="text-muted-foreground size-3.5 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                <p>The address the scaffolded app sends emails from.</p>
                                <p className="text-background/60 mt-1">
                                  Defaults to the client identifier.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Input
                          id="email-sender"
                          value={emailSender}
                          onChange={(e) => {
                            setEmailSender(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
                            emailSenderTouched.current = true;
                          }}
                          placeholder="e.g. acme"
                          maxLength={63}
                        />
                        <p className="text-muted-foreground text-xs">
                          {emailSenderAddress ?? (emailSender || "local-part")}
                        </p>
                        {emailSender && !isValidEmailSender(emailSender) && (
                          <p className="text-destructive text-xs">
                            Must be lowercase alphanumeric and hyphens only, and cannot start or end
                            with a hyphen.
                          </p>
                        )}
                        {emailSender &&
                          isValidEmailSender(emailSender) &&
                          (emailSenderChecking ? (
                            <p className="text-muted-foreground text-xs">
                              Checking availability...
                            </p>
                          ) : (
                            emailSenderAvailable === false && (
                              <p className="text-destructive text-xs">
                                This email sender is already taken.
                              </p>
                            )
                          ))}
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="include-railway" className="text-sm font-medium">
                          Include Railway
                        </Label>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="text-muted-foreground size-3.5 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <p>Deploys frontend and backend as separate Railway services,</p>
                              <p>sets environment variables, and connects to the GitHub repo</p>
                              <p>for automatic deploys on push.</p>
                              <p className="text-background/60 mt-1">
                                Generates a public URL for the frontend.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Auto-provision Railway deployment with frontend and backend services
                      </p>
                    </div>
                    <Switch
                      id="include-railway"
                      checked={includeRailway}
                      onCheckedChange={setIncludeRailway}
                    />
                  </div>

                  {includeRailway && (
                    <div className="space-y-3 rounded-lg border p-3">
                      <div className="flex items-center gap-1.5">
                        <Label className="text-sm font-medium">Custom Domain</Label>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="text-muted-foreground size-3.5 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <p>Custom subdomain for your deployed app.</p>
                              <p className="text-background/60 mt-1">
                                Defaults to the repository slug. A Railway URL is always created as
                                fallback.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="focus-within:ring-ring flex items-center rounded-md border focus-within:ring-1">
                        <input
                          value={customSubdomain}
                          onChange={(e) => {
                            setCustomSubdomain(
                              e.target.value
                                .toLowerCase()
                                .replace(/\s+/g, "-")
                                .replace(/[^a-z0-9-]/g, "")
                                .replace(/-+/g, "-"),
                            );
                            customSubdomainTouched.current = true;
                          }}
                          placeholder={repoSlug || "my-project"}
                          maxLength={63}
                          className="placeholder:text-muted-foreground min-w-0 flex-1 rounded-l-md bg-transparent px-3 py-2 text-sm outline-none"
                        />
                        <span className="text-muted-foreground border-l px-3 py-2 text-sm">
                          .{CUSTOM_DOMAIN_SUFFIX}
                        </span>
                      </div>
                      {customSubdomain && !isValidSlug(customSubdomain) && (
                        <p className="text-destructive text-xs">
                          Subdomain must be 2-63 characters, lowercase alphanumeric and hyphens
                          only, and cannot start or end with a hyphen.
                        </p>
                      )}
                    </div>
                  )}

                  <div
                    className={`mt-2 transition-opacity ${advancedFeatures ? "opacity-100" : "opacity-50 hover:opacity-100"}`}
                  >
                    <div>
                      <h3 className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
                        Advanced Features
                      </h3>
                      <Separator className="mb-3" />
                    </div>

                    <div className="space-y-3 rounded-lg border border-dashed p-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5">
                            <Label htmlFor="advanced-features" className="text-sm font-medium">
                              Advanced Features
                            </Label>
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="text-muted-foreground size-3.5 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Enables engineer-oriented tooling such as</p>
                                  <p>PR review agents powered by AI.</p>
                                  <p className="text-background/60 mt-1">
                                    Leave this off unless you know you need it.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <p className="text-muted-foreground text-xs">
                            For engineers only — adds PR agents and other developer tooling
                          </p>
                        </div>
                        <Switch
                          id="advanced-features"
                          checked={advancedFeatures}
                          onCheckedChange={setAdvancedFeatures}
                        />
                      </div>
                    </div>
                  </div>

                  {error && <p className="text-destructive text-sm">{error}</p>}

                  <div className="flex justify-between">
                    <Button variant="ghost" size="sm" onClick={() => setStep("describe")}>
                      Back
                    </Button>
                    <Button onClick={handleCreate} disabled={!canCreate}>
                      {creating && <Loader2 className="mr-2 size-4 animate-spin" />}
                      {creating ? "Creating..." : "Create Project"}
                      {!creating && (
                        <span className="text-muted-foreground ml-1 hidden text-xs sm:inline">
                          ⌘↵
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogBody>
            </>
          )}

          {step === "progress" && projectId !== null && (
            <>
              <DialogHeader>
                <DialogTitle>Creating your project</DialogTitle>
                <DialogDescription>Sit tight while we set everything up.</DialogDescription>
              </DialogHeader>
              <DialogBody>
                <ScaffoldProgress
                  projectId={projectId}
                  onComplete={handleComplete}
                  onFailed={handleDone}
                  includePersonalization={includePersonalization}
                  includeSupabase={includeSupabase}
                  includeRailway={includeRailway}
                />
                {error && <p className="text-destructive mt-2 text-sm">{error}</p>}
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="text-destructive hover:!bg-destructive/10 hover:!text-destructive"
                  >
                    {cancelling ? "Cancelling..." : "Cancel scaffold"}
                  </Button>
                </div>
              </DialogBody>
            </>
          )}

          {step === "complete" && completionData && (
            <DialogBody className="py-4 text-center">
              {vibeData ? (
                <>
                  <div className="mb-3 flex justify-center">
                    <img
                      src={`data:image/png;base64,${vibeData.image_b64}`}
                      alt={vibeData.animal}
                      className="size-48 rounded-2xl"
                    />
                  </div>
                  <h2 className="mb-1 text-2xl font-normal tracking-tight">{vibeData.heading}</h2>
                  <p className="text-muted-foreground mb-6 text-sm">{vibeData.body}</p>
                </>
              ) : (
                <>
                  <div className="mb-2 flex items-center justify-center gap-3">
                    <PartyPopper className="size-8 text-amber-500" />
                    <Rocket className="text-accent size-8" />
                  </div>
                  <h2 className="mb-1 text-2xl font-normal tracking-tight">Happy vibe coding!</h2>
                  <p className="text-muted-foreground mb-6 text-sm">
                    Your project is live and ready to go. Now go build something amazing.
                  </p>
                </>
              )}

              {(completionData.appUrl || completionData.railwayAppUrl) &&
                (canOpenApp ? (
                  <>
                    <a
                      href={
                        hasCustomDomain && customDomainStatus === "ready"
                          ? completionData.appUrl
                          : (completionData.railwayAppUrl ?? completionData.appUrl)
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mb-3 block"
                    >
                      <Button size="lg" className="w-full gap-2 text-base">
                        <ArrowUpRight className="size-5" />
                        Open Your App
                      </Button>
                    </a>
                    {hasCustomDomain && customDomainStatus !== "ready" && (
                      <p className="text-muted-foreground mb-5 flex items-center justify-center gap-1.5 text-xs">
                        <Loader2 className="size-3 animate-spin" />
                        {customDomainStatus === "timeout"
                          ? `Custom domain pending — ${completionData.appUrl?.replace("https://", "")} may take a few more minutes`
                          : `Setting up ${completionData.appUrl?.replace("https://", "")} (DNS + SSL)…`}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="mb-5">
                    <Button size="lg" className="w-full gap-2 text-base" disabled>
                      <Loader2 className="size-5 animate-spin" />
                      {appReadyStatus === "timeout" && customDomainStatus === "timeout"
                        ? "App is taking longer than expected..."
                        : appReadyStatus === "almost-ready" || customDomainStatus === "almost-ready"
                          ? "Almost there..."
                          : "Deploying your frontend..."}
                    </Button>
                  </div>
                ))}

              {projectId && (
                <Button
                  variant="outline"
                  className="mb-5 w-full gap-2"
                  onClick={() => setInstructionsOpen(true)}
                >
                  <BookOpen className="size-4" /> How to get started
                </Button>
              )}

              <div className="flex flex-wrap justify-center gap-2">
                {completionData.githubUrl && (
                  <a href={completionData.githubUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="gap-1.5">
                      <Github className="size-4" /> GitHub
                    </Button>
                  </a>
                )}
                {completionData.supabaseUrl && (
                  <a href={completionData.supabaseUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="gap-1.5">
                      <ExternalLink className="size-4" /> Supabase
                    </Button>
                  </a>
                )}
                {completionData.railwayUrl && (
                  <a href={completionData.railwayUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="gap-1.5">
                      <ExternalLink className="size-4" /> Railway
                    </Button>
                  </a>
                )}
                {projectId && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          className="gap-1.5"
                          onClick={async () => {
                            try {
                              // Use fetch + temporary anchor so that non-OK
                              // responses (401, 5xx) don't navigate away.
                              const url = `/api/projects/${projectId}/env?slug=${encodeURIComponent(repoSlug)}`;
                              const resp = await fetch(url);
                              if (!resp.ok) {
                                const detail = await resp.text().catch(() => "");
                                logger.error(
                                  "[new-project] env download failed:",
                                  resp.status,
                                  detail,
                                );
                                return;
                              }
                              const blob = await resp.blob();
                              const objectUrl = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = objectUrl;
                              a.download = `${repoSlug}.env`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              URL.revokeObjectURL(objectUrl);
                            } catch (err) {
                              logger.error("[new-project] env download failed:", err);
                            }
                          }}
                        >
                          <Download className="size-4" /> .env
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Place in your project root and rename to{" "}
                          <code className="font-mono">.env</code>
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              {completionData.hasWarnings && completionData.message && (
                <p className="mt-4 text-xs text-amber-600 dark:text-amber-400">
                  {completionData.message}
                </p>
              )}

              <div className="mt-6">
                <Button variant="ghost" size="sm" onClick={handleDone}>
                  Close
                </Button>
              </div>
            </DialogBody>
          )}
        </DialogContent>
      </Dialog>

      {projectId && (
        <InstructionsDialog
          projectId={projectId}
          open={instructionsOpen}
          onOpenChange={setInstructionsOpen}
        />
      )}
    </>
  );
}
